// Dictado por voz del composer de "Crear desde cero".
//
// Se monta EN LUGAR del textarea + fila de controles cuando el user toca el
// mic (AsistenteMode). Arriba va la transcripción en vivo (interina incluida),
// abajo un waveform de historial del nivel del micrófono con los botones
// X (cancelar) y ✓ (confirmar → el texto vuelve al prompt, editable).
//
// Dos APIs de navegador, cada una a lo suyo:
//   • SpeechRecognition (Web Speech API) — la transcripción. Gratis y en
//     tiempo real; Chrome/Edge/Safari. En Firefox no existe: el mic ni se
//     muestra (ver `dictadoDisponible`). Todo el ciclo de vida (re-arranques
//     por silencio incluidos) vive en `iniciarDictado` (dictado.ts), que es
//     donde se testea.
//   • getUserMedia + AnalyserNode — SOLO el waveform. SpeechRecognition no
//     expone niveles de audio, así que se abre un stream aparte.
//
// OJO con el efecto: TODA la cancelación es por variables del closure de esa
// corrida (`activo`, `sesion`, `stream`), nunca refs compartidos. Con un ref,
// el doble montaje de StrictMode en dev dejaba dos reconocimientos vivos
// matándose entre sí (ver ADR-2026-08-24-dictado-strictmode-restart).

import { useEffect, useRef, useState } from 'react';
import { X, Check } from 'lucide-react';
import {
  combinarDictado,
  iniciarDictado,
  nivelDesdeMuestras,
  type ReconocimientoVoz,
} from './dictado';

function constructorReconocimiento(): (new () => ReconocimientoVoz) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => ReconocimientoVoz;
    webkitSpeechRecognition?: new () => ReconocimientoVoz;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** ¿Tiene sentido mostrar el mic en este navegador? */
export function dictadoDisponible(): boolean {
  return (
    constructorReconocimiento() != null &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

// --- Waveform ---------------------------------------------------------------

// Barritas del historial de nivel: 2px de ancho cada 5px, alto mínimo de
// 2px (queda un punto, como los tramos en silencio del mock) y máximo el
// alto del canvas. El historial scrollea: muestra nueva a la derecha.
const BARRA_ANCHO = 2;
const BARRA_PASO = 5;
const MUESTRA_CADA_MS = 50;

const ERROR_PERMISO =
  'No pudimos acceder al micrófono. Revisá los permisos del navegador.';

type PropsDictado = {
  onConfirm: (texto: string) => void;
  onCancel: () => void;
};

export default function DictadoVoz({ onConfirm, onCancel }: PropsDictado) {
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  // Texto SIN la interina — lo que se confirma. Ref y no estado: lo escribe
  // el callback del reconocimiento y lo lee `confirmar`, no la vista.
  const textoRef = useRef('');
  const nivelesRef = useRef<number[]>([]);

  useEffect(() => {
    let activo = true;
    let sesion: { detener: () => void } | null = null;
    let stream: MediaStream | null = null;
    let audioCtx: AudioContext | null = null;
    let raf = 0;

    const Reconocimiento = constructorReconocimiento();
    if (!Reconocimiento) {
      setError('Este navegador no soporta dictado por voz.');
      return;
    }

    // -- Transcripción -------------------------------------------------------
    try {
      sesion = iniciarDictado({
        Reconocimiento,
        lang: 'es-AR',
        onTexto: ({ completo, final }) => {
          textoRef.current = final;
          setTexto(completo);
        },
        onErrorPermiso: () => setError(ERROR_PERMISO),
      });
    } catch {
      setError('No se pudo iniciar el dictado.');
    }

    // -- Waveform ------------------------------------------------------------
    const arrancarWaveform = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        sesion?.detener();
        setError(ERROR_PERMISO);
        return;
      }
      if (!activo) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      audioCtx = new AudioContext();
      // iOS crea el contexto 'suspended' si no nace en el call stack del tap
      // (este efecto corre después): sin resume(), waveform plano en iPhone.
      if (audioCtx.state === 'suspended') {
        void audioCtx.resume().catch(() => {});
      }
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const muestras = new Uint8Array(analyser.fftSize);

      let ultimaMuestra = 0;
      const dibujar = (t: number) => {
        raf = requestAnimationFrame(dibujar);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const { width, height } = canvas.getBoundingClientRect();
        if (width === 0) return;
        if (canvas.width !== Math.round(width * dpr)) {
          canvas.width = Math.round(width * dpr);
          canvas.height = Math.round(height * dpr);
        }
        const capacidad = Math.max(1, Math.floor(width / BARRA_PASO));

        if (t - ultimaMuestra >= MUESTRA_CADA_MS) {
          ultimaMuestra = t;
          analyser.getByteTimeDomainData(muestras);
          const niveles = nivelesRef.current;
          niveles.push(nivelDesdeMuestras(muestras));
          if (niveles.length > capacidad) {
            niveles.splice(0, niveles.length - capacidad);
          }
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = 'rgba(2, 6, 43, 0.4)'; // brand-black al 40%
        const niveles = nivelesRef.current;
        // Ancladas a la derecha: la última muestra pegada a los botones.
        for (let i = 0; i < niveles.length; i++) {
          const x =
            width - (niveles.length - i) * BARRA_PASO + (BARRA_PASO - BARRA_ANCHO);
          const alto = Math.max(2, niveles[i] * height);
          const y = (height - alto) / 2;
          ctx.beginPath();
          ctx.roundRect(x, y, BARRA_ANCHO, alto, BARRA_ANCHO / 2);
          ctx.fill();
        }
      };
      raf = requestAnimationFrame(dibujar);
    };
    void arrancarWaveform();

    return () => {
      activo = false;
      cancelAnimationFrame(raf);
      sesion?.detener();
      stream?.getTracks().forEach((t) => t.stop());
      void audioCtx?.close().catch(() => {});
    };
  }, []);

  // La transcripción larga scrollea sola al fondo: lo nuevo siempre a la vista.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [texto]);

  const confirmar = () => {
    // Preferimos el texto FINAL (sin interina); si el user confirma en plena
    // frase, la interina visible es mejor que nada.
    const final = textoRef.current || texto;
    onConfirm(combinarDictado(final));
  };

  return (
    <div>
      <div
        ref={transcriptRef}
        className="px-5 pt-4 max-h-[120px] overflow-y-auto"
      >
        {error ? (
          <p className="text-[14px] text-red-500/90 leading-snug">{error}</p>
        ) : (
          <p className="italic text-[15.5px] leading-snug min-h-[21px] text-brand-black/60">
            {texto || (
              <span className="text-[#3C3C43]/35 animate-pulse">
                Escuchando…
              </span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 px-3 pb-3 pt-2">
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className={`flex-1 min-w-0 h-9 ${error ? 'invisible' : ''}`}
        />
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancelar dictado"
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2.25} />
        </button>
        {!error && (
          <button
            type="button"
            onClick={confirmar}
            disabled={!texto.trim()}
            aria-label="Usar el texto dictado"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
